import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  ReactNode,
} from 'react';
import axios from 'axios';
import { Cart, CartItem, Product } from '../types';
import { cartApi } from '../services/api';
import { createEmptyCart, loadStoredCart, persistCart } from '../utils/cartStorage';

interface CartActionsContextType {
  addToCart: (product: Product, quantity?: number) => Promise<void>;
  removeFromCart: (productId: string) => Promise<void>;
  updateQuantity: (productId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
}

interface CartStateContextType {
  cart: Cart | null;
  loading: boolean;
  error: string | null;
  getCartItemCount: () => number;
  getCartTotal: () => number;
}

type CartContextType = CartActionsContextType & CartStateContextType;

const CartActionsContext = createContext<CartActionsContextType | undefined>(undefined);
const CartStateContext = createContext<CartStateContextType | undefined>(undefined);

type CartAction =
  | { type: 'SET_CART'; payload: Cart }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_CART' };

const cartReducer = (
  state: { cart: Cart | null; loading: boolean; error: string | null },
  action: CartAction
) => {
  switch (action.type) {
    case 'SET_CART':
      return { ...state, cart: action.payload, loading: false, error: null };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_CART':
      return { ...state, cart: null, error: null };
    default:
      return state;
  }
};

function isNotFoundError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

function commitCart(
  dispatch: React.Dispatch<CartAction>,
  cart: Cart
) {
  persistCart(cart);
  dispatch({ type: 'SET_CART', payload: cart });
}

function buildOptimisticCart(cart: Cart, product: Product, quantity: number): Cart {
  const existingItemIndex = cart.items.findIndex(item => item.productId === product.id);
  let items: CartItem[];

  if (existingItemIndex >= 0) {
    items = cart.items.map((item, index) =>
      index === existingItemIndex
        ? { ...item, quantity: item.quantity + quantity, product }
        : item
    );
  } else {
    items = [...cart.items, { productId: product.id, quantity, product }];
  }

  const total = items.reduce(
    (sum, item) => sum + (item.product?.price ?? 0) * item.quantity,
    0
  );

  return {
    ...cart,
    items,
    total,
    updatedAt: new Date().toISOString(),
  };
}

function buildRemoveOptimisticCart(cart: Cart, productId: string): Cart {
  const items = cart.items.filter(item => item.productId !== productId);
  const total = items.reduce(
    (sum, item) => sum + (item.product?.price ?? 0) * item.quantity,
    0
  );

  return {
    ...cart,
    items,
    total,
    updatedAt: new Date().toISOString(),
  };
}

function buildUpdateQuantityOptimisticCart(cart: Cart, productId: string, quantity: number): Cart {
  const items =
    quantity <= 0
      ? cart.items.filter(item => item.productId !== productId)
      : cart.items.map(item =>
          item.productId === productId ? { ...item, quantity } : item
        );

  const total = items.reduce(
    (sum, item) => sum + (item.product?.price ?? 0) * item.quantity,
    0
  );

  return {
    ...cart,
    items,
    total,
    updatedAt: new Date().toISOString(),
  };
}

function buildClearOptimisticCart(cart: Cart): Cart {
  return {
    ...cart,
    items: [],
    total: 0,
    updatedAt: new Date().toISOString(),
  };
}

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, {
    cart: null,
    loading: false,
    error: null,
  });
  const cartRef = useRef(state.cart);
  cartRef.current = state.cart;

  useEffect(() => {
    const initializeCart = async () => {
      const stored = loadStoredCart();
      if (stored) {
        commitCart(dispatch, stored);
        return;
      }

      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const localCart = createEmptyCart();
        commitCart(dispatch, localCart);
        await cartApi.create(localCart.id);
      } catch {
        const fallback = cartRef.current || createEmptyCart();
        commitCart(dispatch, fallback);
      }
    };

    initializeCart();
  }, []);

  const addToCart = useCallback(async (product: Product, quantity: number = 1) => {
    const currentCart = cartRef.current;
    if (!currentCart) return;

    const optimisticCart = buildOptimisticCart(currentCart, product, quantity);
    commitCart(dispatch, optimisticCart);

    const syncAdd = async (cartId: string) => {
      try {
        await cartApi.addItem(cartId, product.id, quantity);
      } catch (error) {
        if (isNotFoundError(error)) {
          await cartApi.create(cartId);
          await cartApi.addItem(cartId, product.id, quantity);
          return;
        }
        throw error;
      }
    };

    try {
      await syncAdd(currentCart.id);
    } catch {
      // Keep the local cart. Server memory is not durable on serverless hosts.
    }
  }, []);

  const removeFromCart = useCallback(async (productId: string) => {
    const currentCart = cartRef.current;
    if (!currentCart) return;

    const optimisticCart = buildRemoveOptimisticCart(currentCart, productId);
    commitCart(dispatch, optimisticCart);

    try {
      await cartApi.removeItem(currentCart.id, productId);
    } catch (error) {
      if (isNotFoundError(error)) {
        return;
      }
      // Keep local removal so the cart page never gets stuck on a hard error.
    }
  }, []);

  const updateQuantity = useCallback(async (productId: string, quantity: number) => {
    const currentCart = cartRef.current;
    if (!currentCart) return;

    const optimisticCart = buildUpdateQuantityOptimisticCart(currentCart, productId, quantity);
    commitCart(dispatch, optimisticCart);

    try {
      await cartApi.updateItemQuantity(currentCart.id, productId, quantity);
    } catch (error) {
      if (isNotFoundError(error)) {
        return;
      }
    }
  }, []);

  const clearCart = useCallback(async () => {
    const currentCart = cartRef.current;
    if (!currentCart) return;

    const optimisticCart = buildClearOptimisticCart(currentCart);
    commitCart(dispatch, optimisticCart);

    try {
      await cartApi.clear(currentCart.id);
    } catch {
      // Local empty cart is the source of truth.
    }
  }, []);

  const getCartItemCount = useCallback(() => {
    if (!cartRef.current) return 0;
    return cartRef.current.items.reduce((total, item) => total + item.quantity, 0);
  }, []);

  const getCartTotal = useCallback(() => {
    return cartRef.current?.total ?? 0;
  }, []);

  const actionsValue = useMemo(
    () => ({
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
    }),
    [addToCart, removeFromCart, updateQuantity, clearCart]
  );

  const stateValue = useMemo(
    () => ({
      cart: state.cart,
      loading: state.loading,
      error: state.error,
      getCartItemCount,
      getCartTotal,
    }),
    [state.cart, state.loading, state.error, getCartItemCount, getCartTotal]
  );

  return (
    <CartActionsContext.Provider value={actionsValue}>
      <CartStateContext.Provider value={stateValue}>{children}</CartStateContext.Provider>
    </CartActionsContext.Provider>
  );
};

export const useCartActions = (): CartActionsContextType => {
  const context = useContext(CartActionsContext);
  if (context === undefined) {
    throw new Error('useCartActions must be used within a CartProvider');
  }
  return context;
};

export const useCartState = (): CartStateContextType => {
  const context = useContext(CartStateContext);
  if (context === undefined) {
    throw new Error('useCartState must be used within a CartProvider');
  }
  return context;
};

export const useCart = (): CartContextType => {
  return { ...useCartActions(), ...useCartState() };
};
